import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Button, Text } from '@tremor/react';
import { t } from '../lib/i18n';

export interface TourStep {
  /** CSS selector for the element to highlight. First match wins; if the
   *  selector doesn't resolve we still show the dialog centered on screen
   *  so the tour doesn't get stuck. */
  selector?: string;
  title: string;
  body: string;
}

interface Props {
  /** Unique key per tour — used to namespace the localStorage seen-flag. */
  tourKey: string;
  steps: TourStep[];
  /** Called when the tour closes (Skip, Done, Escape, or backdrop click). */
  onClose: () => void;
}

const SEEN_PREFIX = 'paceBI:tour:';

export function markTourSeen(tourKey: string): void {
  try { localStorage.setItem(SEEN_PREFIX + tourKey, '1'); } catch { /* */ }
}
export function hasTourBeenSeen(tourKey: string): boolean {
  try { return localStorage.getItem(SEEN_PREFIX + tourKey) === '1'; }
  catch { return false; }
}
export function resetTourSeen(tourKey: string): void {
  try { localStorage.removeItem(SEEN_PREFIX + tourKey); } catch { /* */ }
}

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

/**
 * Custom step-by-step guided tour with a full-screen backdrop, a glowing
 * outline around the active element, and a floating dialog. No external
 * dep (Shepherd, intro.js) — we control the layout primitives so the
 * tour styling matches Tremor.
 *
 * Lifecycle:
 *   - Mounts → step 0 → user clicks Siguiente → step++ → … → Done
 *   - Escape, Skip, or backdrop click → onClose() (markTourSeen happens
 *     in the parent so the SAME parent decides what "seen" means)
 *
 * The target element rect is recomputed on resize + scroll so the dialog
 * stays anchored even when the user shifts the layout mid-tour.
 */
export default function Tour({ tourKey, steps, onClose }: Props) {
  void tourKey;
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  const step = steps[stepIndex];
  const isLast = stepIndex === steps.length - 1;

  // Recompute the target element's bounding rect on each step and on
  // scroll/resize. Falls back to a centered placement when the selector
  // doesn't resolve — better than crashing the tour.
  useLayoutEffect(() => {
    function compute() {
      if (!step?.selector) { setRect(null); return; }
      const el = document.querySelector(step.selector);
      if (!el) { setRect(null); return; }
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      // Make sure the target is in view — scrolls the page so the
      // highlighted area is centered. block:'center' keeps the dialog
      // visible most of the time.
      el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    }
    compute();
    window.addEventListener('resize', compute);
    window.addEventListener('scroll', compute, true);
    return () => {
      window.removeEventListener('resize', compute);
      window.removeEventListener('scroll', compute, true);
    };
  }, [step]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
      if (e.key === 'ArrowRight' || e.key === 'Enter') {
        if (isLast) onClose();
        else setStepIndex((i) => Math.min(i + 1, steps.length - 1));
      }
      if (e.key === 'ArrowLeft') setStepIndex((i) => Math.max(i - 1, 0));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isLast, onClose, steps.length]);

  if (!step) return null;

  // Dialog placement — below the rect if there's room, else above, else
  // centered. Width fixed at 320px. We bias to "below" because most
  // highlighted targets are in the upper half of the page (headers,
  // KPI tiles, filter cards).
  const dialogStyle: React.CSSProperties = { width: 320 };
  if (rect) {
    const spaceBelow = window.innerHeight - (rect.top + rect.height);
    const placeBelow = spaceBelow > 180 || rect.top < 180;
    if (placeBelow) {
      dialogStyle.top = rect.top + rect.height + 16;
    } else {
      dialogStyle.top = Math.max(16, rect.top - 200);
    }
    // Horizontally: try to center on the target, clamp to viewport.
    const centerX = rect.left + rect.width / 2 - 160;
    dialogStyle.left = Math.max(16, Math.min(centerX, window.innerWidth - 336));
  } else {
    // Centered when no target — translate via inline style so we don't
    // need to size the dialog in advance.
    dialogStyle.top = '50%';
    dialogStyle.left = '50%';
    dialogStyle.transform = 'translate(-50%, -50%)';
  }

  return (
    <>
      {/* Backdrop — single full-screen translucent layer. Clicking it
          closes the tour (matches the dialog modals across the app). */}
      <div
        className="fixed inset-0 z-[60] bg-black/50"
        onClick={onClose}
      />
      {/* Highlight ring around the target. Rendered as a separate fixed
          element rather than mutating the target's classes so we don't
          mess with the target's style cascade. pointer-events-none lets
          clicks fall through to the backdrop. */}
      {rect && (
        <div
          className="fixed z-[61] pointer-events-none rounded ring-4 ring-tremor-brand ring-offset-2 ring-offset-tremor-background"
          style={{
            top: rect.top - 4,
            left: rect.left - 4,
            width: rect.width + 8,
            height: rect.height + 8,
          }}
        />
      )}
      {/* Dialog — z above the backdrop + ring. */}
      <div
        ref={dialogRef}
        className="fixed z-[62] bg-tremor-background rounded-tremor-default shadow-xl border border-tremor-border p-4"
        style={dialogStyle}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-baseline gap-2 mb-1">
          <Text className="text-tremor-content text-xs">
            {stepIndex + 1} {t.tour.of} {steps.length}
          </Text>
          <button
            type="button"
            onClick={onClose}
            className="ml-auto text-tremor-content hover:text-tremor-content-strong text-xl leading-none"
            aria-label={t.tour.skip}
          >×</button>
        </div>
        <div className="font-semibold text-tremor-content-strong mb-1">{step.title}</div>
        <Text className="text-tremor-content text-sm">{step.body}</Text>
        <div className="mt-4 flex items-center gap-2">
          <Button
            size="xs"
            variant="light"
            disabled={stepIndex === 0}
            onClick={() => setStepIndex((i) => Math.max(i - 1, 0))}
          >
            {t.tour.prev}
          </Button>
          <Button
            size="xs"
            variant="light"
            onClick={onClose}
          >
            {t.tour.skip}
          </Button>
          <div className="ml-auto" />
          <Button
            size="xs"
            variant="primary"
            onClick={() => {
              if (isLast) onClose();
              else setStepIndex((i) => Math.min(i + 1, steps.length - 1));
            }}
          >
            {isLast ? t.tour.done : t.tour.next}
          </Button>
        </div>
      </div>
    </>
  );
}
